// The languages the FIRST screen may speak — decided before React mounts,
// and loaded before it does (audit 10/09 bis, perf n°4: one chunk per
// language, French alone in the trunk).
//
// Order of truth, and it is the SAME as the two consumers it serves:
//   · the site (pages/site/SiteApp): the address prefix (`/de/autun`), else on
//     a bare address the guest's REMEMBERED choice — never the device
//     language, a crawler declares English and `/` stays the French home;
//   · the app (pages/MedardExperience, lib/langPreference.resolveInitialLang):
//     `?lang=` > the remembered choice > the DEVICE language > French. The app
//     is what a phone gets, what the site gate closed gets, and what the
//     app's own doors get on a wide screen (`/livre`, `/reveil`, `/admin`…,
//     `?display=book|tablet`).
//
// The shell's inline hint (plugins/preload-lang.js) mirrors this function in
// ES5 so the chunk is requested beside main.js, not after it; a test runs
// both on one grid of inputs and refuses any disagreement.
import { parseSitePath } from './siteRoutes';
import { resolveInitialLang, LANG_STORE_KEY } from './langPreference';
import { isOfferedLang, loadLangs } from './i18n';
import { matchesMobileViewport } from './viewport';
import { siteEnabled } from './siteGate';
import routes from './experienceRoutes.json';

const SKIP = new RegExp(routes.skipPattern);
const LIVRE = new RegExp(routes.livrePattern);

/** The `?display=` value of a search string, or null. */
function displayOf(search) {
    try {
        return new URLSearchParams(search).get('display');
    } catch {
        return null;
    }
}

/**
 * Does this address speak the DEVICE language on a wide screen? True for the
 * app's own doors — the pages that are NOT the site (`parseSitePath` names
 * none of them) plus `/livre` and the fixed editions that mount the app.
 * Pure; shared with `bootLangs` and mirrored by the shell hint.
 */
export function usesDeviceLang(pathname, search = '') {
    if (SKIP.test(pathname) || LIVRE.test(pathname)) return true;
    const display = displayOf(search);
    return display !== null && routes.displayMounts.includes(display);
}

/**
 * Does this address MOUNT the guest experience on a phone (or with the site
 * gate closed)? False for the back-offices (/admin, /chantier), the AR pilot,
 * the kiosk route, the legal notice a phone renders alone, and the fixed
 * editions under `?display=` other than the Book and the tablet. Shared by
 * App.js (`wantsExperience`) and the shell's hint (plugins/preload-experience,
 * through the same experienceRoutes.json).
 */
export function mountsExperience(pathname, search = '') {
    if (SKIP.test(pathname)) return false;
    const display = displayOf(search);
    return display === null || routes.displayMounts.includes(display);
}

/**
 * The offered languages, other than French, that the first render may need.
 * Pure: every input is passed in. Usually one code, at most two (a site
 * address in one language opened by a guest who remembers another).
 */
export function bootLangs({ pathname = '/', search = '', stored = null, navLangs = [], phone = false } = {}) {
    const parsed = parseSitePath(pathname);
    let urlLang;
    try {
        urlLang = new URLSearchParams(search).get('lang') || undefined;
    } catch {
        urlLang = undefined;
    }
    const codes = new Set();
    if (parsed.lang && parsed.lang !== 'fr') codes.add(parsed.lang);
    const device = phone || usesDeviceLang(pathname, search);
    codes.add(resolveInitialLang({ urlLang, stored, navLangs: device ? navLangs : [] }));
    codes.delete('fr');
    return [...codes].filter(isOfferedLang);
}

/** `bootLangs` read from the live window — null-safe for SSR and tests. */
export function bootLangsFromWindow() {
    if (typeof window === 'undefined') return [];
    let stored = null;
    try {
        stored = window.localStorage.getItem(LANG_STORE_KEY);
    } catch { /* private mode */ }
    const navLangs = (typeof navigator !== 'undefined'
        && (navigator.languages || (navigator.language ? [navigator.language] : []))) || [];
    return bootLangs({
        pathname: window.location.pathname,
        search: window.location.search,
        stored,
        navLangs,
        phone: matchesMobileViewport() || !siteEnabled(),
    });
}

/**
 * Load what the first screen needs. ALWAYS resolves: a chunk that never
 * comes must not cost the page — `t()` answers in French meanwhile, which is
 * a worse first screen than the right language, never a blank one.
 */
export function loadBootLangs() {
    return loadLangs(bootLangsFromWindow()).catch(() => []);
}
